import { cn } from "@lib/cn"
import { Loader2 } from "lucide-react"
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react"

type Variant = "primary" | "secondary" | "ghost" | "outline" | "destructive"
type Size = "sm" | "md" | "lg"

/**
 * Roles, never palette steps (PRD-60 chunk 0). Mirrors the professor SPA's
 * Button so the two apps read as one product. `destructive` is new: three
 * tables and ConfirmDialog used to paint their delete button with a red
 * className override on `primary`, which is how a hue stops meaning anything.
 */
const variantClasses: Record<Variant, string> = {
  primary: "bg-accent text-ink-inverted hover:bg-accent-hover",
  secondary: "bg-inset text-ink hover:bg-border/60",
  ghost: "text-ink-muted hover:bg-inset hover:text-ink",
  outline: "border border-border text-ink-muted hover:border-border-strong hover:bg-inset",
  destructive: "bg-danger text-ink-inverted hover:bg-danger-hover",
}

/**
 * Touch first, density second. Every size clears the 44px target on a phone
 * and tightens from `md` (768px) up, the same breakpoint as the professor
 * SPA, so a dense row on a laptop stays dense while the same button stays
 * tappable. Row actions use `md` (36px at `md+`), not `sm` (32px).
 */
const sizeClasses: Record<Size, string> = {
  sm: "min-h-11 gap-1.5 px-2.5 text-xs md:min-h-8",
  md: "min-h-11 gap-2 px-3.5 text-sm md:min-h-9",
  lg: "min-h-12 gap-2 px-5 text-base",
}

/** The same look for a router `<Link>` — navigation stays an anchor. */
export function buttonClasses({
  variant = "primary",
  size = "md",
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}) {
  return cn(
    "rounded-control inline-flex items-center justify-center font-medium transition-colors duration-150",
    variantClasses[variant],
    sizeClasses[size],
    className,
  )
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      disabled,
      leftIcon,
      rightIcon,
      children,
      onClick,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      // Loading is `aria-disabled`, not `disabled` (PRD-60 chunk B): a
      // disabled button drops keyboard focus to <body> the moment it is
      // pressed. The click is swallowed instead, which also blocks a second
      // submit through Enter.
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      className={buttonClasses({
        variant,
        size,
        className: cn(
          "disabled:cursor-not-allowed disabled:opacity-60 aria-busy:cursor-progress",
          className,
        ),
      })}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  ),
)
Button.displayName = "Button"
